import classNames from 'classnames'
import {useMemo, useRef, memo} from 'react'
import {graphql, useFragment} from 'react-relay'

import {useBuildHrefWithSaveParams} from '../../../hooks/routes'
import {stringifyId} from '../../../types'
import {getDuration, getStatsAroundBuild, getTimeInQueue} from '../../../utils/buildStats'
import {displayDate} from '../../../utils/dateTimeUtils'
import {emptyArray} from '../../../utils/empty'
import {buildGraphHint} from '../../packages/Hints/HintItems/Hints.buildOverview'
import {useHint} from '../../packages/Hints/Hints.hooks'
import IconButton from '../IconButton/IconButton'
import RouterLinkWrapper from '../RouterLinkWrapper/RouterLinkWrapper'
import {useVisibility} from '../Visible/Visible.hooks'

import {MAX_HEIGHT, SAVED_BUILD_QUERY_PARAMS} from './BuildStatsBar/BuildStatsBar'
import BuildStatsBar from './BuildStatsBar/BuildStatsBar.container'
import type {Props, LoaderProps} from './BuildsTimeline.types'

import styles from './BuildsTimeline.css'

const ANIMATION_STEP = 70 //ms

function BuildsTimelineLoader({className, statCount, withPreviousNextButton}: LoaderProps) {
  const height = MAX_HEIGHT * 2 + 1
  const ref = useRef<HTMLDivElement>(null)
  const isVisible = useVisibility(ref)
  const loaderClasses = classNames(styles.loadingBar, {
    [styles.loadingBarAnimate]: isVisible,
  })
  return (
    <div
      data-builds-timeline-loader
      className={classNames(className, styles.builds, {
        [styles.loadingWithPreviousNextButton]: withPreviousNextButton === true,
      })}
      ref={ref}
      style={{
        height,
      }}
    >
      {Array(statCount)
        .fill(undefined)
        .map((_, i) => (
          <div // eslint-disable-next-line react/no-array-index-key
            key={i}
            className={loaderClasses}
            style={{
              height: (MAX_HEIGHT * (i + 1)) / statCount,
              animationDelay: `${i * ANIMATION_STEP}ms`,
              marginBottom: MAX_HEIGHT + 1,
            }}
          />
        ))}
    </div>
  )
}

const fragment = graphql`
  fragment BuildsTimelineFragment on Builds {
    build {
      rawId
      state
      startDate
      queuedDate
      finishDate
      runningInfo {
        elapsedSeconds
      }
      ...BuildStatsBarFragment
    }
  }
`

function BuildsTimeline({
  className,
  location,
  buildsKey,
  statCount,
  currentBuildId,
  hoveredBuildId,
  hoverBuild,
  selectBuild,
  loading,
  withSaveQueryParams,
  withPreviousNextButton,
  withHighLightCurrentBuild,
  isVisible,
  aroundBuild,
}: Props) {
  const buildsData = useFragment(fragment, buildsKey)
  const builds = buildsData?.build ?? emptyArray
  const stats = useMemo(
    () => (aroundBuild != null ? getStatsAroundBuild(builds, aroundBuild, statCount) : builds),
    [aroundBuild, builds, statCount],
  )

  const currentBuildIndex = stats.findIndex(({rawId}) => rawId === currentBuildId)
  const previousBuildId = currentBuildIndex === -1 ? null : stats[currentBuildIndex + 1]?.rawId
  const nextBuildId = currentBuildIndex === -1 ? null : stats[currentBuildIndex - 1]?.rawId

  useHint(
    isVisible && !loading && !(withPreviousNextButton === true && stats.length === 1)
      ? buildGraphHint
      : null,
  )
  const height = MAX_HEIGHT * 2 + 1
  const previousBuildHref = useBuildHrefWithSaveParams(
    location,
    previousBuildId,
    withSaveQueryParams === true ? SAVED_BUILD_QUERY_PARAMS : [],
  )
  const nextBuildHref = useBuildHrefWithSaveParams(
    location,
    nextBuildId,
    withSaveQueryParams === true ? SAVED_BUILD_QUERY_PARAMS : [],
  )

  const nextButton = useMemo(
    () =>
      withPreviousNextButton === true && (
        <RouterLinkWrapper>
          <IconButton
            className={styles.nextButton}
            icon="chevron-right"
            href={nextBuildHref}
            disabled={!nextBuildId}
            title="Next build"
            text
          />
        </RouterLinkWrapper>
      ),
    [nextBuildHref, nextBuildId, withPreviousNextButton],
  )

  const prevButton = useMemo(
    () =>
      withPreviousNextButton === true && (
        <RouterLinkWrapper>
          <IconButton
            className={styles.previousButton}
            icon="chevron-left"
            href={previousBuildHref}
            disabled={!previousBuildId}
            title="Previous build"
            text
          />
        </RouterLinkWrapper>
      ),
    [previousBuildHref, previousBuildId, withPreviousNextButton],
  )

  if (loading) {
    return (
      <BuildsTimelineLoader
        className={className}
        statCount={statCount}
        withPreviousNextButton={withPreviousNextButton}
      />
    )
  }

  if (withPreviousNextButton === true && stats.length === 1) {
    return null
  }

  const maxDuration = Math.max(...stats.map(getDuration))
  const maxTimeInQueue = Math.max(...stats.map(getTimeInQueue))
  const earliest = stats[stats.length - 1]
  const reversed = [...stats].reverse()
  return reversed.length > 0 ? (
    <div
      className={classNames(className, styles.wrrapper)}
      style={{
        height,
      }}
      data-hint-container-id={buildGraphHint.id}
    >
      {prevButton}
      <div className={styles.builds}>
        {reversed.map(stat => (
          <BuildStatsBar
            key={stringifyId(stat.rawId)}
            id={stat.rawId}
            state={stat.state}
            buildKey={stat}
            maxDuration={maxDuration}
            maxTimeInQueue={maxTimeInQueue}
            isCurrent={withHighLightCurrentBuild === true && currentBuildId === stat.rawId}
            isHovered={hoveredBuildId === stat.rawId}
            hoverBuild={hoverBuild}
            selectBuild={selectBuild}
            withSaveQueryParams={withSaveQueryParams}
          />
        ))}
        {earliest && (
          <div className={styles.earliestWrapper}>
            <span className={styles.earliest}>{displayDate(earliest.startDate ?? '')}</span>
          </div>
        )}
      </div>
      {nextButton}
    </div>
  ) : null
}

export default memo(BuildsTimeline)
